import React, { useEffect } from "react";
import { Navbar } from "../../components/Navbar";
import { Footer } from "../../components/Footer";
import { HeaderProgress } from "./Header";
import { ProgressGroup } from "./ProgressGroup";
import { ProgressTitle } from "./ProgressTitle";
import { ProgressTable } from "./ProgressTable";
import { useMoodle } from "../../contexts/MoodleContext";
import { ProgressChartItem } from "./ProgressChart";
import { ProgressRowItem } from "./ProgressRowItem";
import { ProgressRowItemDetail } from "./ProgressRowItemDetail";
import { useAuth } from "../../contexts/AuthContext";
import { baseURL } from "../../services/apirest";
import { useTheme } from "../../contexts/ThemeContext";
import { readableColor } from "polished";
import { useConfig } from "../../contexts/config";
import { mapWithConcurrency } from "../../utils/concurrency";
import { resolveCourseCertificateCmid } from "../../services/moodle/courseCertificates";
import { resolveCourseGradeAndProgress } from "../../services/moodle/courseFinalGrade";
import { getUserProgressSummary } from "../../services/moodle/courses";
import { KpiCard } from "./KpiCard";
import { Skeleton } from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { filterAllowedCourses } from "../../utils/courses";
import WhatsAppButton from "../../components/WhatsAppButton";

const CERT_CONCURRENCY = 6;

const Progress = (/* {data} */) => {
  const [loadingGrades, setLoadingGrades] = React.useState(true);
  const [sortConfig, setSortConfig] = React.useState({ key: null, direction: "asc" });
  const moodle = useMoodle();
  const theme = useTheme();
  const { lang, user } = useAuth();
  const { config } = useConfig()
  const [sessionKey, setSessionKey] = React.useState(null);
  const [contrastColor, setContrastColor] = React.useState("#fff");
  const [certificates, setCertificates] = React.useState({});
  const [finalGrades, setFinalGrades] = React.useState({});
  const [courseProgress, setCourseProgress] = React.useState({});
  const [summaryData, setSummaryData] = React.useState(null);

  const [selectedCourseId, setSelectedCourseId] = React.useState(null);
  const [selectedCourseName, setSelectedCourseName] = React.useState("");
  const [detailData, setDetailData] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailTitles, setDetailTitles] = React.useState(null);
  const [pluginState, setPluginState] = React.useState("loading"); // "loading" | "success" | "failed"

  const filteredMoodleCourses = React.useMemo(
    () => (moodle.courses ? filterAllowedCourses(moodle.courses) : null),
    [moodle.courses]
  );

  const courseIdsKey = React.useMemo(
    () => (filteredMoodleCourses ?? []).map((c) => c.id).join(","),
    [filteredMoodleCourses]
  );

  // Fast path: Consolidated server-side summary from local_tipddy_performance plugin
  useEffect(() => {
    if (!user?.token) return;
    let cancelled = false;

    getUserProgressSummary({ token: user.token, userid: user.id || 0 })
      .then((data) => {
        if (!cancelled) {
          if (data && data.courses && Array.isArray(data.courses)) {
            setSummaryData(data);
            setPluginState("success");
            setLoadingGrades(false);
          } else {
            setPluginState("failed");
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn("Tipddy Performance plugin not active yet, using fallback resolution:", err.message);
          setPluginState("failed");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user?.token, user?.id]);

  useEffect(() => {
    if (pluginState !== "failed") return;
    const courses = filteredMoodleCourses;
    if (!courses || !courses.length || !user?.token) return;

    let cancelled = false;
    setCertificates({});

    mapWithConcurrency(
      courses,
      CERT_CONCURRENCY,
      (c) => resolveCourseCertificateCmid({ token: user.token, courseId: c.id }),
      (c, _i, cmid) => {
        if (!cancelled) {
          setCertificates((prev) => ({ ...prev, [c.id]: cmid }));
        }
      }
    );

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pluginState, courseIdsKey, user?.token]);

  useEffect(() => {
    if (pluginState !== "failed") return;
    const courses = filteredMoodleCourses ?? [];
    if (!courses.length || !user?.token) {
      if (moodle.courses) {
        setLoadingGrades(false);
      }
      return;
    }

    let cancelled = false;
    setLoadingGrades(true);
    setFinalGrades({});
    setCourseProgress({});

    mapWithConcurrency(
      courses,
      CERT_CONCURRENCY,
      (c) => resolveCourseGradeAndProgress({ token: user.token, id: user.id, courseId: c.id }),
      (c, _i, result) => {
        if (!cancelled) {
          setFinalGrades((prev) => ({ ...prev, [c.id]: result?.finalGrade ?? null }));
          setCourseProgress((prev) => ({ ...prev, [c.id]: result?.progress ?? null }));
        }
      }
    ).finally(() => {
      if (!cancelled) {
        setLoadingGrades(false);
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pluginState, courseIdsKey, user?.token, user?.id]);

  const tableTitles = React.useMemo(() => {
    if (!lang || !lang["History"]) return null;
    return [
      { label: lang["History"].first_column, key: "name", sortable: true },
      { label: lang["History"].progress_column, key: "progress", sortable: true, width: "110px" },
      { label: lang["History"].second_column, key: "grade", sortable: true, width: "130px" },
      { label: lang["History"].third_column, key: "startdate", sortable: true, width: "120px" },
      { label: lang["History"].fourth_column, key: "enddate", sortable: true, width: "140px" },
      { label: lang["History"].fifth_column, key: "certificate", sortable: true, width: "140px" },
      ...(config?.opciones?.includes("encuesta") ? [{ label: lang["History"].sixth_column, key: "survey", sortable: false, width: "100px" }] : []),
    ];
  }, [lang, config]);

  const handleSort = React.useCallback((key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      const defaultDir = key === "name" ? "asc" : "desc";
      return { key, direction: defaultDir };
    });
  }, []);

  useEffect(() => {
    !moodle.courses && moodle.getCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (theme && theme.styles?.bg_course_group) {
      if (
        theme.styles?.bg_course_group?.backgroundColor === "rgba(0, 0, 0, 0.05)"
      ) {
        setContrastColor("#1A1A1ACC");
      } else {
        setContrastColor(
          readableColor(theme.styles?.bg_course_group?.backgroundColor)
        );
      }
    }
  }, [theme]);

  const visibleCourses = React.useMemo(() => {
    const raw = summaryData?.courses ?? filteredMoodleCourses ?? [];
    return filterAllowedCourses(raw);
  }, [summaryData, filteredMoodleCourses]);

  const kpis = React.useMemo(() => {
    const total = visibleCourses.length;
    const completed = visibleCourses.filter((c) => {
      if (c.is_completed !== undefined) {
        return c.is_completed;
      }
      const certCmid =
        c.certificate_cmid && c.certificate_cmid > 0
          ? c.certificate_cmid
          : certificates[c.id];
      const prog = certCmid
        ? 100
        : (courseProgress[c.id] ??
          (c.progress !== null && c.progress !== undefined
            ? Number(c.progress)
            : null));
      return prog !== null && Number(prog) >= 100;
    }).length;

    const inProgress = visibleCourses.filter((c) => {
      if (c.is_completed !== undefined) {
        const grade = c.final_grade ?? "-";
        return (
          !c.is_completed &&
          ((c.progress !== null && Number(c.progress) > 0) || grade !== "-")
        );
      }
      const certCmid =
        c.certificate_cmid && c.certificate_cmid > 0
          ? c.certificate_cmid
          : certificates[c.id];
      const prog = certCmid
        ? 100
        : (courseProgress[c.id] ??
          (c.progress !== null && c.progress !== undefined
            ? Number(c.progress)
            : null));
      return prog !== null && Number(prog) > 0 && Number(prog) < 100;
    }).length;

    return { total, completed, inProgress };
  }, [visibleCourses, certificates, courseProgress]);

  const sortedCourses = React.useMemo(() => {
    if (!sortConfig.key) return visibleCourses;

    const { key, direction } = sortConfig;
    const modifier = direction === "asc" ? 1 : -1;

    return [...visibleCourses].sort((a, b) => {
      if (key === "name") {
        const nameA = a.displayname || a.fullname || "";
        const nameB = b.displayname || b.fullname || "";
        return nameA.localeCompare(nameB, "es", { sensitivity: "base", numeric: true }) * modifier;
      }

      if (key === "progress") {
        const getProg = (c) => {
          const certCmid = (c.certificate_cmid && c.certificate_cmid > 0) ? c.certificate_cmid : certificates[c.id];
          if (certCmid) return 100;
          if (typeof c.progress === "number") return c.progress;
          const p = courseProgress[c.id] ?? (c.progress !== null && c.progress !== undefined ? Number(c.progress) : null);
          return p !== null && !isNaN(p) ? Number(p) : -1;
        };
        const progA = getProg(a);
        const progB = getProg(b);
        if (progA !== progB) {
          return (progA - progB) * modifier;
        }
        return (a.displayname || "").localeCompare(b.displayname || "", "es");
      }

      if (key === "grade") {
        const getGrade = (c) => {
          const raw = c.final_grade ?? (finalGrades[c.id]?.formatted ?? "-");
          if (!raw || raw === "-") return null;
          const cleaned = String(raw).replace(",", ".").replace(/[^0-9.]/g, "");
          const parsed = parseFloat(cleaned);
          return isNaN(parsed) ? null : parsed;
        };
        const gradeA = getGrade(a);
        const gradeB = getGrade(b);
        const hasGradeA = gradeA !== null;
        const hasGradeB = gradeB !== null;
        if (hasGradeA !== hasGradeB) {
          return hasGradeA ? -1 : 1;
        }
        if (!hasGradeA && !hasGradeB) {
          return (a.displayname || "").localeCompare(b.displayname || "", "es");
        }
        const diff = gradeA - gradeB;
        return diff !== 0 ? diff * modifier : (a.displayname || "").localeCompare(b.displayname || "", "es");
      }

      if (key === "startdate") {
        const dateA = a.startdate ? Number(a.startdate) : 0;
        const dateB = b.startdate ? Number(b.startdate) : 0;
        if (dateA !== dateB) {
          return (dateA - dateB) * modifier;
        }
        return (a.displayname || "").localeCompare(b.displayname || "", "es");
      }

      if (key === "enddate") {
        const hasEndA = Boolean(a.enddate && Number(a.enddate) !== 0);
        const hasEndB = Boolean(b.enddate && Number(b.enddate) !== 0);
        if (hasEndA !== hasEndB) {
          return hasEndA ? -1 : 1;
        }
        if (!hasEndA && !hasEndB) {
          return (a.displayname || "").localeCompare(b.displayname || "", "es");
        }
        const diff = Number(a.enddate) - Number(b.enddate);
        return diff !== 0 ? diff * modifier : (a.displayname || "").localeCompare(b.displayname || "", "es");
      }

      if (key === "certificate") {
        const hasCert = (c) => {
          const certCmid = (c.certificate_cmid && c.certificate_cmid > 0) ? c.certificate_cmid : certificates[c.id];
          return certCmid ? 1 : 0;
        };
        const certA = hasCert(a);
        const certB = hasCert(b);
        if (certA !== certB) {
          return (certA - certB) * modifier;
        }
        return (a.displayname || "").localeCompare(b.displayname || "", "es");
      }

      return 0;
    });
  }, [visibleCourses, sortConfig, certificates, finalGrades, courseProgress]);

  React.useEffect(() => {
    if (lang && !detailTitles) {
      setDetailTitles([
        { label: lang["History"].inner_first_column },
        { label: lang["History"].inner_second_column, width: "120px" },
        ...(config?.opciones?.includes("feedback") ? [
          { label: lang["History"].inner_fourth_column, width: "180px" },
          { label: lang["History"].inner_fifth_column, width: "180px" },
        ] : []),
      ]);
    }
  }, [lang, config, detailTitles]);

  async function fetchDetail(courseId) {
    setDetailLoading(true);
    setDetailData(null);
    try {
      const [gradesData] = await moodle.getProgressDetail(courseId);
      const rawItems = gradesData?.usergrades?.[0]?.gradeitems ?? [];
      const filterdata = rawItems
        .filter((d) => d.itemtype !== "course" && d.itemtype !== "category")
        .filter((d) => d.itemmodule !== "folder")
        .filter((d) => d.itemname !== null && d.itemname !== "");
      setDetailData(filterdata);
    } catch (e) {
      console.log(e.message);
      setDetailData([]);
    } finally {
      setDetailLoading(false);
    }
  }

  const handleRowClick = (course) => {
    setSelectedCourseId(course.id);
    setSelectedCourseName(course.displayname);
    fetchDetail(course.id);
  };

  const handleBack = () => {
    setSelectedCourseId(null);
    setSelectedCourseName("");
    setDetailData(null);
  };

  return (
    <>
      {lang && (
        <>
          <Navbar />
          {/* {sessionKey && (
            <iframe
              src={`${baseURL}auth/userkey/login.php?key=${sessionKey}&wantsurl=${baseURL}`}
              id="login-token"
              frameBorder="0"
              width="0"
              allow="autoplay ; microphone; camera"
              height={"0"}
              title="login-token"
            />
          )} */}

          <div
            className="flex flex-col items-center w-full px-4 py-48 min-h-dvh"
            style={{
              backgroundColor: theme.colors.backgroundColorBody
                ? theme.colors.backgroundColorBody
                : theme.mode === "light"
                ? "rgba(0, 0, 0, 0.05)"
                : "rgb(34, 34, 34)", // TODO: Cambiar Styles por Colors
              gap: "36px",
            }}
          >
            <div className="container w-full space-y-3">
              <style>{`
                .progress-panels-wrapper {
                  position: relative;
                  overflow: hidden;
                }
                .progress-panel {
                  width: 100%;
                  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
                  will-change: transform;
                }
                .progress-panel-list {
                  transform: translateX(0);
                  opacity: 1;
                }
                .progress-panel-list.slide-out {
                  transform: translateX(-100%);
                  opacity: 0;
                  position: absolute;
                  top: 0; left: 0; right: 0;
                  pointer-events: none;
                }
                .progress-panel-detail {
                  transform: translateX(100%);
                  opacity: 0;
                  position: absolute;
                  top: 0; left: 0; right: 0;
                  pointer-events: none;
                }
                .progress-panel-detail.slide-in {
                  transform: translateX(0);
                  opacity: 1;
                  position: relative;
                  pointer-events: auto;
                }
                .progress-row-hover:hover {
                  background-color: ${theme.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)"};
                }
              `}</style>

              <div className="flex items-center gap-3">
                {selectedCourseId && (
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold transition-colors duration-150"
                    style={{
                      backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                      color: theme.colors.textColorDefault,
                    }}
                  >
                    <ArrowBackIcon sx={{ fontSize: 18 }} />
                    Volver
                  </button>
                )}
                <p
                  className="font-medium text-4xl"
                  style={{
                    color: readableColor(
                      theme.colors.backgroundColorBody
                        ? theme.colors.backgroundColorBody
                        : theme.mode === "light"
                        ? "rgba(0, 0, 0, 0.05)"
                        : "rgb(34, 34, 34)"
                    ),
                  }}
                >
                  {selectedCourseId ? selectedCourseName : lang["History"].first_title}
                </p>
              </div>

              {!selectedCourseId && (
                <div className="flex flex-wrap gap-4 w-full pb-2">
                  <KpiCard
                    label={lang["History"].kpi_total_courses}
                    value={
                      loadingGrades ? (
                        <Skeleton
                          variant="text"
                          width={48}
                          sx={{ bgcolor: theme.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)" }}
                        />
                      ) : (
                        kpis.total
                      )
                    }
                    icon={<SchoolIcon />}
                    color={"#F4E8FF"}
                  />
                  <KpiCard
                    label={lang["History"].kpi_completed_courses}
                    value={
                      loadingGrades ? (
                        <Skeleton
                          variant="text"
                          width={48}
                          sx={{ bgcolor: theme.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)" }}
                        />
                      ) : (
                        kpis.completed
                      )
                    }
                    icon={<CheckCircleIcon />}
                    color={"#DCFCE7"}
                  />
                  <KpiCard
                    label={lang["History"].kpi_courses_in_progress}
                    value={
                      loadingGrades ? (
                        <Skeleton
                          variant="text"
                          width={48}
                          sx={{ bgcolor: theme.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)" }}
                        />
                      ) : (
                        kpis.inProgress
                      )
                    }
                    icon={<TrendingUpIcon />}
                    color={"#D5F4F8"}
                  />
                </div>
              )}

              <div className="progress-panels-wrapper">
                {/* Panel: lista de cursos */}
                <div className={`progress-panel progress-panel-list${selectedCourseId ? " slide-out" : ""}`}>
                  <ProgressTable
                    headers={tableTitles}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  >
                    {(!summaryData && !moodle.courses) || loadingGrades ? (
                      [...Array(6)].map((_, i) => (
                        <tr key={`skel-${i}`} style={{
                          backgroundColor: i % 2 === 1
                            ? (theme.mode === "dark" ? "rgba(255,255,255,0.05)" : "#F5F5F5")
                            : (theme.mode === "dark" ? "rgba(255,255,255,0.02)" : "#FFFFFF"),
                        }}>
                          {[...Array(6)].map((_, j) => (
                            <td key={j} className="p-[24px] border-none">
                              <Skeleton
                                variant="rectangular"
                                height={20}
                                sx={{
                                  borderRadius: "4px",
                                  bgcolor: theme.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
                                  width: j === 0 ? "80%" : j === 1 ? "80px" : "60%",
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      sortedCourses.map((c, idx) => {
                        const grade = c.final_grade ?? (finalGrades[c.id]?.formatted ?? "-");
                        const certCmid = (c.certificate_cmid && c.certificate_cmid > 0)
                          ? c.certificate_cmid
                          : certificates[c.id];
                        const prog = certCmid
                          ? 100
                          : (typeof c.progress === "number"
                              ? c.progress
                              : (courseProgress[c.id] ?? (c.progress !== null && c.progress !== undefined ? Number(c.progress) : null)));
                        return (
                          <ProgressRowItem
                            key={c.id ?? idx}
                            course={c}
                            grade={grade}
                            progress={prog}
                            lang={lang}
                            certificateCmid={certCmid}
                            includeEncuesta={config.opciones.includes("encuesta")}
                            rowIndex={idx}
                            onRowClick={handleRowClick}
                          />
                        );
                      })
                    )}
                  </ProgressTable>
                </div>

                {/* Panel: detalle del curso */}
                <div className={`progress-panel progress-panel-detail${selectedCourseId ? " slide-in" : ""}`}>
                  {detailLoading ? (
                    <div className="flex flex-col gap-2 p-4">
                      {[...Array(5)].map((_, i) => (
                        <Skeleton
                          key={i}
                          variant="rectangular"
                          height={48}
                          sx={{
                            borderRadius: "6px",
                            bgcolor: theme.mode === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.08)",
                          }}
                        />
                      ))}
                    </div>
                  ) : detailData && detailTitles ? (
                    detailData.length > 0 ? (
                      <ProgressTable headers={detailTitles}>
                        {detailData.map((d, idx) => (
                          <ProgressRowItemDetail
                            key={idx}
                            detail={d}
                            index={idx}
                            includeFeedback={config.opciones.includes("feedback")}
                          />
                        ))}
                      </ProgressTable>
                    ) : (
                      <div
                        className="flex flex-col items-center justify-center p-12 rounded-xl text-center shadow-sm"
                        style={{
                          backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.03)" : "#FFFFFF",
                          border: `1px solid ${theme.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
                        }}
                      >
                        <p
                          className="font-lato font-medium text-[15px]"
                          style={{ color: theme.mode === "dark" ? "#B0B0B0" : "#4B5563" }}
                        >
                          {lang["History"]?.no_evaluations || "Este curso se aprueba por visualización de contenidos y no registra calificaciones numéricas."}
                        </p>
                      </div>
                    )
                  ) : null}
                </div>
              </div>
            </div>

            {/* <div className="container w-full">
              <p
                className="font-medium text-4xl"
                style={{
                  color: readableColor(
                    theme.colors.backgroundColorBody
                      ? theme.colors.backgroundColorBody
                      : theme.mode === "light"
                      ? "rgba(0, 0, 0, 0.05)"
                      : "rgb(34, 34, 34)"
                  ),
                }}
              >
                {lang["History"].second_title}
              </p>
              <ul className="progress-list mt-4">
                <li
                  className="progress-list"
                  style={{ color: theme.mode === "light" ? "#16232c" : "#fff" }}
                >
                  <div
                    className="p-[5px] border-[1px] border-solid border-[#23232333] rounded-md mr-2"
                    style={{
                      backgroundColor:
                        theme.mode === "dark"
                          ? theme.colors.backgroundPendienteLight
                          : theme.colors.backgroundPendienteDark, // TODO: Cambiar Palette por Colors
                      borderColor: contrastColor+33,
                    }}
                  />
                  &nbsp;{lang["History"].complete}
                  <div
                    className="p-[5px] border-[1px] border-solid border-[#23232333] rounded-md ml-5"
                    style={{
                      backgroundColor:
                        theme.mode === "light"
                          ? "rgba(0, 0, 0, 0.05)"
                          : "rgb(34, 34, 34)", // TODO: Cambiar Palette por Colors
                      borderColor: contrastColor+33,
                    }}
                  />
                  &nbsp;{lang["History"].pending}
                </li>
              </ul>
            </div>
            <div className="container w-full">
              <ProgressGroup>
                {moodle.courses &&
                  moodle.courses.map((c, idx) => {
                    // if (c.id > 371 || c.id === 355) {
                    if(c.id > 389 || c.id === 385 || c.id === 387){
                      return (
                        <ProgressChartItem
                          key={idx}
                          course={c}
                          lang={lang}
                          
                        />
                      );
                    } else {
                      return null;
                    }
                  })}
              </ProgressGroup>
            </div> */}
          </div>
          <WhatsAppButton phoneNumber="56997034800" />
        </>
      )}
    </>
  );
};

// return (
//   <>
//     {lang && (
//       <>
//         <Navbar />
//         {sessionKey && (
//           <iframe
//             src={`${baseURL}auth/userkey/login.php?key=${sessionKey}&wantsurl=${baseURL}`}
//             id="login-token"
//             frameBorder="0"
//             width="0"
//             allow="autoplay ; microphone; camera"
//             height={"0"}
//             title="login-token"
//           />
//         )}
//         <div style={styles.progressBlock}>
//           <div style={styles.wrapper}>
//             <ProgressTitle title={lang["History"].first_title} />
//             <ProgressTable headers={tableTitles}>
//               {moodle.courses &&
//                 grades &&
//                 moodle.courses.map((c, idx) => {
//                   let grade = grades.filter((g) => g.courseid === c.id)[0]
//                     ?.grade
//                     ? grades.filter((g) => g.courseid === c.id)[0].grade
//                     : "-";
//                   console.log(c)
//                   return (
//                     <ProgressRowItem
//                       key={idx}
//                       course={c}
//                       grade={grade}
//                       lang={lang}
//                     />
//                   );
//                 })}
//             </ProgressTable>
//           </div>
//           <HeaderProgress lang={lang} />
//           <div className="container w-full">
//             <ProgressGroup>
//               {moodle.courses &&
//                 moodle.courses.map((c, idx) => {
//                   return <ProgressChartItem key={idx} course={c} lang={lang} />;
//                 })}
//             </ProgressGroup>
//           </div>
//         </div>
//         <Footer />
//       </>
//     )}
//   </>
// );
// const styles = {
//   wrapper: {
//     paddingTop: "12rem",
//   },
//   progressBlock: {
//     justifyContent: "center",
//     width: "80%",
//     marginLeft: "10%",
//     paddingBottom: "0",
//   },
// };

export default Progress;
